import React, { useState } from 'react';
import Button from '../../components/Button';
import { MentionBaseProvider } from '../../editor/mentions/MentionBase';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { Comment_CommentFragment } from '../../graphql/generated/graphql';
import type { ExtractCommentThreadTypes } from '../../utils/post/threadUtils';
import MagneticWallSheet from '../MagneticWallSheet';
import {
  COMMENT_THREAD_CREATION_ID,
  usePendingMagneticWallItem,
} from '../PendingMagneticWallItemProvider';
import useMagneticWallSheetContext from '../useMagneticWallSheetContext';
import Comment from './Comment';
import CommentCreate from './CommentCreate';
import { useCommentThreadState } from './CommentThread';
import * as styles from './CommentThreadSheet.module.scss';
import FoldedComments, { defaultRenderMoreText } from './FoldedComments';
import useResolveThread from './useResolveThread';

export const CommentThreadSheetContext = ({
  id,
  type,
  highlight,
}: Parameters<typeof useMagneticWallSheetContext>[0]) => {
  const { highlighted, before, after } = useMagneticWallSheetContext({
    type,
    id,
    highlight,
  });

  return highlighted && highlighted.length > 0 ? (
    <MentionBaseProvider containerType="comment">
      <div className={styles.sheetContextContainer}>
        {before}
        {highlighted}
        {after}
      </div>
    </MentionBaseProvider>
  ) : null;
};

const POST_FRAGMENT = gql(`
  fragment CommentThreadSheet_post on RootPostFields {
    ...Comment_post
    ...CommentCreate_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentThreadSheet_thread on RootThreadFields {
    id
    ... on CommentThread {
      comments {
        id
        ...Comment_comment
      }
    }
    ... on PublicCommentThread {
      comments {
        id
        ...Comment_comment
      }
    }
    ...CommentCreate_thread
    ...useResolveThread_thread
  }
`);

const FOCUSED_COMMENT_EXPAND_LIMIT = 6;

interface CommentThreadSheetProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  thread: ExtractCommentThreadTypes<typeof THREAD_FRAGMENT>;
  onClose: () => void;
  createComment: React.ComponentProps<typeof CommentCreate>['createComment'];
  addComment: React.ComponentProps<typeof CommentCreate>['addComment'];
  deleteThread: React.ComponentProps<typeof Comment>['deleteThread'];
  deleteComment: (commentId: string) => void;
  toggleCommentReaction: (
    comment: ExtractCommentThreadTypes<
      typeof THREAD_FRAGMENT
    >['comments'][number],
    emoji: string,
  ) => void;
}

const CommentThreadSheet = ({
  post,
  onClose,
  thread,
  createComment,
  addComment,
  deleteThread,
  deleteComment,
  toggleCommentReaction,
}: CommentThreadSheetProps) => {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { commentThreadState, setCommentThreadState } = useCommentThreadState(
    thread.id,
  );
  const { setPendingCommentThreadRange } = usePendingMagneticWallItem();

  const resolveThread = useResolveThread(thread);

  const isCreating = thread.id === COMMENT_THREAD_CREATION_ID;

  return (
    <MagneticWallSheet
      onClose={() => {
        onClose();
        setPendingCommentThreadRange(null);
      }}
      title={isCreating ? 'New comment' : 'Comment'}
      headerContent={
        <CommentThreadSheetContext id={thread.id} type="comment" highlight />
      }
      inputRow={
        <CommentCreate
          post={post}
          thread={thread}
          onChange={setCommentThreadState}
          initialContent={commentThreadState}
          disabled={editingCommentId !== null}
          createComment={createComment}
          addComment={addComment}
        />
      }
    >
      {!isCreating && (
        <>
          {thread.comments && thread.comments.length > 0 && (
            <FoldedComments.Root<Comment_CommentFragment>
              comments={thread.comments}
              expanded={isExpanded}
              limit={FOCUSED_COMMENT_EXPAND_LIMIT}
              renderMoreText={(count) => (
                <Button variant="link" onClick={() => setIsExpanded(true)}>
                  {defaultRenderMoreText(count)}
                </Button>
              )}
              renderComment={(comment, idx) => (
                <Comment
                  key={comment.id}
                  expanded
                  first={idx === 0}
                  last={idx === thread.comments.length - 1}
                  comment={comment}
                  thread={thread}
                  onResize={() => {}}
                  post={post}
                  editing={editingCommentId === comment.id}
                  onEdit={() => setEditingCommentId(comment.id)}
                  onCancelEdit={() => setEditingCommentId(null)}
                  onResolve={resolveThread}
                  deleteThread={deleteThread}
                  deleteComment={() => deleteComment(comment.id)}
                  toggleCommentReaction={(emoji) =>
                    toggleCommentReaction(comment, emoji)
                  }
                />
              )}
            />
          )}
        </>
      )}
    </MagneticWallSheet>
  );
};

export default CommentThreadSheet;
